import React, { useState } from 'react';
import { Award, Download, CheckCircle, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import API_URL from '../../lib/apiConfig';

// ARC chat-native certificate card.
//
// The backend auto-awards the certification on a final-test pass and writes
// one ai_chat_artifacts doc with artifact_type="certificate" plus a chat
// message carrying the [[artifact:…]] marker. ChatArtifact.jsx fetches the
// doc and dispatches it here. The card renders the credential summary and a
// Download button that hits the owner-scoped PDF endpoint
// (GET /api/users/me/certifications/{id}/pdf) — the same one the
// Certifications page uses, so the chat download and the page download are
// byte-identical.

function formatIssued(iso) {
  if (!iso || typeof iso !== 'string') return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export default function CertificateArtifact({ artifact }) {
  const payload = artifact?.payload || {};
  const { certification_id, title, recipient, final_test_score, issued_at, download_url } = payload;

  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);

  const handleDownload = async () => {
    const path =
      download_url ||
      (certification_id ? `/api/users/me/certifications/${certification_id}/pdf` : null);
    if (!path) return;
    setDownloading(true);
    setError(false);
    try {
      const blob = await api.download(`${API_URL}${path}`);
      if (!blob) {
        setError(true);
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Derive the filename from the cert title (shared certification_title on
      // the backend) so a future cert type isn't mislabelled "AI-Recruiter-…".
      const safeName =
        (title || 'Certificate').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') ||
        'Certificate';
      a.download = `${safeName}-${certification_id || 'certificate'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(true);
    } finally {
      setDownloading(false);
    }
  };

  if (!certification_id) {
    return (
      <div className="my-3 p-4 rounded-xl border border-boss-border bg-boss-elevated text-sm text-boss-text-secondary">
        Certificate details are unavailable.
      </div>
    );
  }

  return (
    <div className="my-3 rounded-xl border border-violet-500/30 bg-boss-elevated overflow-hidden">
      <div className="px-5 py-4 bg-gradient-to-r from-violet-600/20 to-fuchsia-600/20 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center flex-shrink-0">
            <Award size={20} className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-boss-text truncate">
              {title || 'AI Recruiter Certification'}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
              <CheckCircle size={12} />
              {recipient ? `Certified · ${recipient}` : 'Certified'}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          data-testid="certificate-download"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
          <Download size={14} />
          {downloading ? 'Preparing…' : 'Download PDF'}
        </button>
      </div>
      <div className="px-5 py-3 text-xs text-boss-text-secondary space-y-1">
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {typeof final_test_score === 'number' && (
            <span>
              Final score: <strong className="text-boss-text">{Math.round(final_test_score)}%</strong>
            </span>
          )}
          {issued_at && <span>Issued {formatIssued(issued_at)}</span>}
        </div>
        {error && (
          <div
            data-testid="certificate-download-error"
            className="mt-2 flex items-start gap-1.5 text-red-600 dark:text-red-400"
          >
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>Download failed — please try again.</span>
          </div>
        )}
      </div>
    </div>
  );
}
